import React from "react";
import type { SimpleTableConfig, BaseEntity, FieldValue } from "../types";
import type { AuthenticatedUser } from "@/lib/types/unified";
import type { ComposedTableCallbacks } from "../SimpleTableContextValue/callbacks-slice";
import { useSelectionCallbacks } from "./callbacks/useSelectionCallbacks";
import { useSortCallbacks } from "./callbacks/useSortCallbacks";
import { useColumnCallbacks } from "./callbacks/useColumnCallbacks";
import { useDragDropCallbacks } from "./callbacks/useDragDropCallbacks";
import { useInlineEditCallbacks } from "./callbacks/useInlineEditCallbacks";
import { useBulkEditCallbacks } from "./callbacks/useBulkEditCallbacks";
import { useAdvancedFilterCallbacks } from "./callbacks/useAdvancedFilterCallbacks";
import { useScrollSyncCallbacks } from "./callbacks/useScrollSyncCallbacks";

/**
 * Props for useSimpleTableCallbacks hook
 */
export interface UseSimpleTableCallbacksProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  data: TData[];
  state: ReturnType<typeof import("./useSimpleTableState").useSimpleTableState<TData>>;
  appUser: AuthenticatedUser;
  onInlineEdit?: (payload: Record<string, FieldValue>) => Promise<void>;
  onBulkAction?: (actionId: string, selectedItems: TData[]) => void;
}

/**
 * Everything this hook produces.
 *
 * Four of the context's callbacks are NOT here: `handleSelectAll`,
 * `handleBulkAction`, `handleBodyScroll` and `handleHeaderScroll`. This hook
 * cannot build them — the first two need `paginatedData` and the last two need
 * the table's DOM refs, neither of which it is given — so it exposes the
 * factories/two-argument forms below and `SimpleTable` closes over the missing
 * arguments. `SimpleTable` then assembles the full `ComposedTableCallbacks`,
 * which is where `tsc` checks the set is complete.
 */
export type SimpleTableCallbacksResult<TData extends BaseEntity> = Omit<
  ComposedTableCallbacks<TData>,
  "handleSelectAll" | "handleBulkAction" | "handleBodyScroll" | "handleHeaderScroll"
> & {
  createHandleSelectAll: () => (selected: boolean, paginatedData: TData[]) => void;
  createHandleBulkAction: () => (actionId: string, paginatedData: TData[]) => void;
  handleBodyScroll: (
    tableBodyRef: React.RefObject<HTMLDivElement | null>,
    tableHeaderScrollRef: React.RefObject<HTMLDivElement | null>
  ) => void;
  handleHeaderScroll: (
    tableBodyRef: React.RefObject<HTMLDivElement | null>,
    tableHeaderScrollRef: React.RefObject<HTMLDivElement | null>
  ) => void;
};

/**
 * Composes the per-concern callback hooks in `hooks/callbacks/` into the set
 * `SimpleTable` publishes on its context.
 *
 * Those hooks existed and were fully implemented; nothing called them. This hook
 * used to hand-roll five members (two of which were empty placeholders) and the
 * context slice destructured twenty-seven names off the result, so row
 * selection, sorting, cell editing, column resize/reorder, bulk edit and the
 * advanced-filter controls were all reading `undefined`. The explicit return
 * type above is what keeps that from recurring: adding a callback to the
 * context and forgetting to supply it here no longer compiles.
 */
export function useSimpleTableCallbacks<TData extends BaseEntity>({
  config,
  data,
  state,
  appUser,
  onInlineEdit,
  onBulkAction,
}: UseSimpleTableCallbacksProps<TData>): SimpleTableCallbacksResult<TData> {
  const selection = useSelectionCallbacks<TData>({
    selectedItems: state.selectedItems,
    setSelectedItems: state.setSelectedItems,
    onBulkAction,
  });

  const sort = useSortCallbacks<TData>({
    config,
    sortField: state.sortField,
    sortColumns: state.sortColumns,
    setSortField: state.setSortField,
    setSortDirection: state.setSortDirection,
    setSortColumns: state.setSortColumns,
  });

  const columns = useColumnCallbacks<TData>({
    config,
    setColumnWidths: state.setColumnWidths,
  });

  const dragDrop = useDragDropCallbacks({
    draggedColumn: state.draggedColumn,
    columnOrder: state.columnOrder,
    setColumnOrder: state.setColumnOrder,
    setDraggedColumn: state.setDraggedColumn,
  });

  const inlineEdit = useInlineEditCallbacks<TData>({
    config,
    data,
    editValue: state.editValue,
    appUser,
    onInlineEdit,
    setEditingCell: state.setEditingCell,
    setEditValue: state.setEditValue,
    setSavingCell: state.setSavingCell,
  });

  const bulkEdit = useBulkEditCallbacks<TData>({ data, onInlineEdit });

  const advancedFilters = useAdvancedFilterCallbacks<TData>({ config });

  const scrollSync = useScrollSyncCallbacks();

  return {
    // Selection
    handleSelectItem: selection.handleSelectItem,
    createHandleSelectAll: selection.createHandleSelectAll,
    createHandleBulkAction: selection.createHandleBulkAction,

    // Sorting
    handleSort: sort.handleSort,
    removeSortColumn: sort.removeSortColumn,
    clearAllSorting: sort.clearAllSorting,

    // Columns
    handleColumnResizeCallback: columns.handleColumnResizeCallback,
    resetColumnWidthsCallback: columns.resetColumnWidthsCallback,

    // Column drag/drop reordering
    handleDragStartCallback: dragDrop.handleDragStartCallback,
    handleDragOverCallback: dragDrop.handleDragOverCallback,
    handleDropCallback: dragDrop.handleDropCallback,
    handleDragEndCallback: dragDrop.handleDragEndCallback,

    // Inline cell editing
    handleCellEditCallback: inlineEdit.handleCellEditCallback,
    handleCellSaveCallback: inlineEdit.handleCellSaveCallback,
    handleCellCancelCallback: inlineEdit.handleCellCancelCallback,

    // Bulk edit
    handleBulkEditField: bulkEdit.handleBulkEditField,
    handleBulkEditAllFiltered: bulkEdit.handleBulkEditAllFiltered,

    // Advanced filters + the datetime custom-range modal
    addAdvancedFilter: advancedFilters.addAdvancedFilter,
    removeAdvancedFilter: advancedFilters.removeAdvancedFilter,
    clearAllAdvancedFilters: advancedFilters.clearAllAdvancedFilters,
    handleCustomRangeClick: advancedFilters.handleCustomRangeClick,
    handleCustomRangeApply: advancedFilters.handleCustomRangeApply,
    handleCustomRangeClose: advancedFilters.handleCustomRangeClose,

    // Horizontal scroll sync (SimpleTable binds the refs)
    handleBodyScroll: scrollSync.handleBodyScroll,
    handleHeaderScroll: scrollSync.handleHeaderScroll,
  };
}
